import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getMomentAccountId, subscribeMomentAccount } from '../moments/accountScope';
import { FramedBox, FramedSwitch, FramedText } from '../presets/Framed';
import { requestIntimacy } from './client';
import styles from './intimacy.module.css';
import { intimacyControlMeta } from './layout';

export const INTIMACY_NOTICE = '开启后，角色一开始将以陌生人的身份回答用户，随着用户的对话，角色好感度可能会增加，可能会降低。好感度不可见，仅可通过和角色的对话语气、内容感受。';

export function IntimacyControl({ presetId, top }: { presetId: string; top: number }): React.ReactElement {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(true);
  const [notice, setNotice] = useState(false);
  const [error, setError] = useState('');
  const revision = useRef(0);
  const pending = useRef(false);
  const dismiss = useRef<HTMLButtonElement>(null);
  const meta = intimacyControlMeta(top);
  useEffect(() => {
    let abort = new AbortController();
    const invalidate = (): number => ++revision.current;
    const refresh = (reset = false): void => {
      abort.abort(); abort = new AbortController();
      const id = invalidate();
      pending.current = false; setError('');
      if (reset) { setEnabled(false); setNotice(false); }
      if (!getMomentAccountId()) { setEnabled(false); setBusy(false); return; }
      setBusy(true);
      void requestIntimacy(presetId, undefined, abort.signal).then(state => {
        if (id === revision.current) setEnabled(state.enabled);
      }).catch(failure => {
        if (id === revision.current && !abort.signal.aborted) setError(failure instanceof Error ? failure.message : '亲密系统加载失败');
      }).finally(() => { if (id === revision.current) setBusy(false); });
    };
    refresh(true);
    const unsubscribe = subscribeMomentAccount(() => refresh(true));
    const onFocus = (): void => { if (!pending.current) refresh(); };
    window.addEventListener('focus', onFocus);
    const timer = window.setInterval(onFocus, 60_000);
    return () => { invalidate(); abort.abort(); unsubscribe(); window.removeEventListener('focus', onFocus); window.clearInterval(timer); };
  }, [presetId]);
  useEffect(() => {
    if (!notice) return;
    dismiss.current?.focus();
    const close = (event: KeyboardEvent): void => { if (event.key === 'Escape') setNotice(false); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [notice]);
  const toggle = async (next: boolean): Promise<void> => {
    if (busy || pending.current) return;
    pending.current = true; setBusy(true); setError('');
    const id = ++revision.current;
    try {
      const state = await requestIntimacy(presetId, next);
      if (id !== revision.current) return;
      setEnabled(state.enabled);
      if (state.enabled) setNotice(true);
    } catch (failure) {
      if (id === revision.current) setError(failure instanceof Error ? failure.message : '亲密系统保存失败，请重试');
    } finally { if (id === revision.current) { pending.current = false; setBusy(false); } }
  };
  return <>
    <FramedText meta={meta.label} className="zs-field-label">亲密系统</FramedText>
    <FramedSwitch meta={meta.switch}
      className={`zs-switch zs-media-switch${enabled ? ' zs-switch-on' : ''}`} checked={enabled} disabled={busy} onToggle={next => void toggle(next)}>
      <span className="zs-media-switch-thumb" />
    </FramedSwitch>
    <FramedBox meta={meta.status}>
      <span className={error ? styles.error : styles.hint} role={error ? 'alert' : 'status'}>{error || (busy ? '正在同步亲密系统' : !getMomentAccountId() ? '登录后可开启亲密系统' : '状态自动同步；好感度仅通过对话感受。')}</span>
    </FramedBox>
    {notice ? createPortal(<div className={styles.overlay}>
      <section className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby="intimacy-title" aria-describedby="intimacy-notice" onKeyDown={event => { if (event.key === 'Tab') { event.preventDefault(); dismiss.current?.focus(); } }}>
        <h2 id="intimacy-title">亲密系统</h2><p id="intimacy-notice">{INTIMACY_NOTICE}</p>
        <button ref={dismiss} type="button" onClick={() => setNotice(false)}>知道了</button>
      </section>
    </div>, document.body) : null}
  </>;
}
